import { useMemo, useCallback, useState } from 'react';
import { motion } from 'framer-motion';
import {
  RefreshCw,
  TrendingUp,
  FileSpreadsheet,
  Wallet,
  Scale,
  Building2,
  Calendar,
  Settings2,
} from 'lucide-react';
import { useAuthStore } from '@/shared/stores/authStore';
import { useDashboardUIStore } from '@/shared/stores/ui';
import { usePreliminarStore } from '@/shared/stores/preliminarStore';
import type { Compania } from '@/shared/types';
import { useDashboard, usePeriodosCerrados } from './hooks';
import { PnLTree } from './PnLTree';
import { PeriodComparisonDialog } from './PeriodComparisonDialog';
import { PnLTrendsChart } from './PnLTrendsChart';
import { buildKpis, extractPeriodColumns, buildPnLTree } from './pnlUtils';
import { PnLReportActions } from '@/features/reports/pnl';
import { KpiCard } from '@/shared/components/KpiCard';
import { PreliminarToggle } from '@/shared/components/PreliminarToggle';
import { Button } from '@/shared/ui/button/Button';
import { Card } from '@/shared/ui/card/Card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select/Select';
import { Loading } from '@/shared/ui/loading/Loading';
import { EmptyState } from '@/shared/ui/empty-state/EmptyState';
import { formatCurrency, formatNumber, cn } from '@/shared/lib/utils';

const EMPTY_ARRAY: Compania[] = [];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' as const } },
};

export function DashboardPage() {
  const selectedCompany = useAuthStore(state => state.selectedCompany);
  const user = useAuthStore(state => state.user);
  const companias = user?.companias ?? EMPTY_ARRAY;
  const setSelectedCompany = useAuthStore(state => state.setSelectedCompany);

  const {
    periodo,
    cantidadMeses,
    selectedMonths,
    setPeriodo,
    setCantidadMeses,
    setSelectedMonths,
  } = useDashboardUIStore();

  const compania = selectedCompany?.codigo || '';
  const incluirAbierto = usePreliminarStore(state => state.incluirAbierto);

  const {
    data: periodosCerrados = [],
    isLoading: loadingPeriodos,
  } = usePeriodosCerrados(compania, incluirAbierto);

  const dashboardParams = useMemo(
    () => ({
      compania,
      periodo,
      cantidad_meses: cantidadMeses,
      incluir_abierto: incluirAbierto,
    }),
    [compania, periodo, cantidadMeses, incluirAbierto]
  );

  const {
    data: rows = [],
    isLoading,
    refetch,
    isFetching,
  } = useDashboard(dashboardParams);

  const periodColumns = useMemo(() => extractPeriodColumns(rows), [rows]);

  const realColumns = useMemo(
    () => periodColumns.filter(c => !c.isBudget && c.date),
    [periodColumns]
  );

  // Meses disponibles para comparar (solo columnas reales, orden cronológico).
  const availableMonths = useMemo(
    () => realColumns.map(c => c.yearMonth),
    [realColumns]
  );

  // Por defecto seleccionar los últimos 4 meses disponibles.
  const effectiveSelectedMonths = useMemo(() => {
    if (selectedMonths.length > 0) {
      // Filtrar solo los que aún existen en los datos.
      const valid = selectedMonths.filter(m => availableMonths.includes(m));
      return valid.length > 0 ? valid : availableMonths.slice(-4);
    }
    return availableMonths.slice(-4);
  }, [selectedMonths, availableMonths]);

  const kpis = useMemo(
    () => buildKpis(rows, periodColumns),
    [rows, periodColumns]
  );

  const [gastosView, setGastosView] = useState<'detalle' | 'clasificacion'>('detalle');

  const tree = useMemo(() => buildPnLTree(rows), [rows]);

  // Periodos abiertos (preliminares) según el catálogo con opt-in.
  const preliminarMonths = useMemo(
    () => periodosCerrados.filter(p => p.estado === 'REVISION').map(p => p.value.slice(0, 7)),
    [periodosCerrados]
  );

  const reportData = useMemo(
    () => ({
      companyName: selectedCompany?.codigo || 'Empresa',
      periodLabel: periodo,
      periodColumns,
      selectedMonths: effectiveSelectedMonths,
      tree,
      rows,
      gastosView,
      preliminarMonths,
    }),
    [selectedCompany, periodo, periodColumns, effectiveSelectedMonths, tree, rows, gastosView, preliminarMonths]
  );

  const handleCantidadMesesChange = useCallback(
    (value: string) => {
      setCantidadMeses(Number(value));
      setSelectedMonths([]);
    },
    [setCantidadMeses, setSelectedMonths]
  );

  const handleCompanyChange = useCallback(
    (codigo: string) => {
      const company = companias.find(c => c.codigo === codigo);
      if (company) {
        setSelectedCompany(company);
        setPeriodo('');
      }
    },
    [companias, setSelectedCompany]
  );

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header toolbar */}
      <motion.div
        variants={itemVariants}
        className="flex flex-col gap-4 rounded-2xl border border-border bg-surface p-4 shadow-elevated sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="font-display text-2xl font-bold text-text-primary">
            Comparativo Mensual
          </h1>
          <p className="text-sm text-text-secondary">
            Estado de resultados con histórico mensual y presupuesto.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 rounded-xl border border-border bg-surface-elevated/50 px-3 py-2">
            <Building2 className="h-4 w-4 text-primary" />
            <Select
              value={selectedCompany?.codigo || ''}
              onValueChange={handleCompanyChange}
            >
              <SelectTrigger className="h-9 w-[180px] border-0 bg-transparent text-sm font-semibold focus:ring-0">
                <SelectValue placeholder="Compañía" />
              </SelectTrigger>
              <SelectContent>
                {companias.map(c => (
                  <SelectItem key={c.codigo} value={c.codigo}>
                    {c.codigo}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2 rounded-xl border border-border bg-surface-elevated/50 px-3 py-2">
            <Calendar className="h-4 w-4 text-secondary" />
            <Select
              value={periodo}
              onValueChange={setPeriodo}
              disabled={!compania || loadingPeriodos}
            >
              <SelectTrigger className="h-9 w-[160px] border-0 bg-transparent text-sm font-semibold focus:ring-0">
                <SelectValue placeholder="Periodo" />
              </SelectTrigger>
              <SelectContent>
                {periodosCerrados.map(p => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.textContent}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2 rounded-xl border border-border bg-surface-elevated/50 px-3 py-2">
            <Settings2 className="h-4 w-4 text-secondary" />
            <Select
              value={String(cantidadMeses)}
              onValueChange={handleCantidadMesesChange}
              disabled={!periodo}
            >
              <SelectTrigger className="h-9 w-[140px] border-0 bg-transparent text-sm font-semibold focus:ring-0">
                <SelectValue placeholder="Histórico" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="6">6 meses</SelectItem>
                <SelectItem value="12">12 meses</SelectItem>
                <SelectItem value="24">24 meses</SelectItem>
                <SelectItem value="36">36 meses</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2 rounded-xl border border-border bg-surface-elevated/50 px-3 py-2">
            <PreliminarToggle />
          </div>

          <div className="flex items-center gap-2 rounded-xl border border-border bg-surface-elevated/50 px-3 py-2">
            <PeriodComparisonDialog
              availableMonths={availableMonths}
              selectedMonths={effectiveSelectedMonths}
              onChange={setSelectedMonths}
              disabled={availableMonths.length === 0}
            />
          </div>

          <PnLReportActions data={reportData} disabled={isLoading || rows.length === 0} />

          <Button
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
            Actualizar
          </Button>
        </div>
      </motion.div>

      {/* KPIs grid */}
      <motion.div
        variants={itemVariants}
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
      >
        <KpiCard
          title="Ventas Netas"
          value={
            kpis.ventasNetas.value !== null
              ? formatCurrency(Math.abs(kpis.ventasNetas.value))
              : '—'
          }
          icon={TrendingUp}
          variant="primary"
          change={kpis.ventasNetas.change ?? undefined}
          delay={0}
        />
        <KpiCard
          title="Volumen Total"
          value={
            kpis.volumen.value !== null
              ? formatNumber(Math.abs(kpis.volumen.value), 2)
              : '—'
          }
          icon={FileSpreadsheet}
          variant="secondary"
          change={kpis.volumen.change ?? undefined}
          delay={0.1}
        />
        <KpiCard
          title="Valor Agregado / KG"
          value={
            kpis.valorAgregado.value !== null
              ? formatNumber(kpis.valorAgregado.value, 4)
              : '—'
          }
          icon={Scale}
          variant="accent"
          change={kpis.valorAgregado.change ?? undefined}
          delay={0.2}
        />
        <KpiCard
          title="Colón / KG Promedio"
          value={
            kpis.colonKg.value !== null
              ? formatNumber(kpis.colonKg.value, 4)
              : '—'
          }
          icon={Wallet}
          variant="primary"
          change={kpis.colonKg.change ?? undefined}
          delay={0.3}
        />
      </motion.div>

      {/* Active company mini-card */}
      <motion.div variants={itemVariants}>
        <Card className="flex items-center justify-between gap-4 border-l-4 border-l-secondary p-5">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary-muted text-secondary">
              <Building2 className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-text-muted">
                Compañía activa
              </p>
              <p className="font-display text-xl font-bold text-text-primary">
                {selectedCompany?.codigo || 'Ninguna seleccionada'}
              </p>
            </div>
          </div>
          <div className="hidden text-right sm:block">
            <p className="text-xs font-bold uppercase tracking-wider text-text-muted">
              Periodo
            </p>
            <p className="font-display text-lg font-semibold text-text-primary">
              {periodo || 'Sin periodo'}
            </p>
          </div>
        </Card>
      </motion.div>

      {/* Trends chart */}
      {rows.length > 0 && (
        <motion.div variants={itemVariants}>
          <PnLTrendsChart rows={rows} />
        </motion.div>
      )}

      {/* Table */}
      <motion.div variants={itemVariants} className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display text-xl font-bold text-text-primary">
              Estado financiero detallado
            </h2>
            <p className="text-sm text-text-secondary">
              Desglose mensual por concepto de cuenta.
            </p>
          </div>
        </div>

        {isLoading ? (
          <Card className="flex items-center justify-center p-16">
            <Loading size="xl" />
          </Card>
        ) : rows.length > 0 ? (
          <PnLTree
            rows={rows}
            selectedMonths={effectiveSelectedMonths}
            gastosView={gastosView}
            onGastosViewChange={setGastosView}
            preliminarMonths={preliminarMonths}
          />
        ) : periodo ? (
          <EmptyState
            title="Sin datos para el periodo"
            description="Selecciona otra compañía o periodo para visualizar el estado financiero."
            icon={<TrendingUp className="h-7 w-7" />}
          />
        ) : (
          <EmptyState
            title="Selecciona un periodo"
            description="El dashboard se mostrará automáticamente al seleccionar compañía y periodo."
            icon={<Calendar className="h-7 w-7" />}
          />
        )}
      </motion.div>
    </motion.div>
  );
}
